import type { ServiceItem, ServicesProps } from "./services";
import { SmartLink } from "../smart-link";
import { cn } from "../../lib/utils";
import { hasText } from "./services-text";

/** The props every alternative services layout reads. */
type ServicesLayoutProps = Pick<ServicesProps, "id" | "className" | "heading" | "subheading" | "backgroundColor"> & {
  services?: ServiceItem[];
};

const SURFACE_RADIUS_STYLE = { borderRadius: "var(--jobsite-radius)" } as const;

/** The section around a layout: its padding, and the background the owner picked or the page's. */
const ServicesSection = ({ id, className, backgroundColor, children }: Pick<ServicesLayoutProps, "id" | "className" | "backgroundColor"> & { children: React.ReactNode }): React.JSX.Element => (
  <section
    id={id}
    className={cn("jobsite:overflow-x-clip jobsite:py-16 jobsite:md:py-20", hasText(backgroundColor) ? "" : "jobsite:bg-background", className)}
    style={hasText(backgroundColor) ? { backgroundColor } : undefined}
  >
    <div className="jobsite:container jobsite:mx-auto jobsite:px-4">{children}</div>
  </section>
);

/** The heading and optional subheading, centred like the other services layouts. */
const ServicesHeading = ({ heading, subheading, className }: Pick<ServicesLayoutProps, "heading" | "subheading" | "className">): React.JSX.Element => (
  <div className={cn("jobsite:mx-auto jobsite:mb-10 jobsite:max-w-3xl jobsite:text-center", className)}>
    <h2 className="jobsite:font-heading jobsite:text-3xl jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground jobsite:sm:text-4xl jobsite:md:text-5xl">
      {heading}
    </h2>
    {hasText(subheading) && (
      <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-pretty jobsite:text-muted-foreground jobsite:md:text-xl">{subheading}</p>
    )}
  </div>
);

/** A service's name after a dot in the brand color, linked to its page when it has one. */
const ServiceName = ({ service, className }: { service: ServiceItem; className?: string }): React.JSX.Element => (
  <span className={cn("jobsite:flex jobsite:items-center jobsite:gap-2.5", className)}>
    <span aria-hidden="true" className="jobsite:size-2 jobsite:shrink-0 jobsite:rounded-full jobsite:bg-primary" />
    {hasText(service.href) ? (
      <SmartLink to={service.href ?? ""} className="jobsite:underline-offset-4 jobsite:hover:text-primary jobsite:hover:underline">
        {service.title}
      </SmartLink>
    ) : service.title}
  </span>
);

/** One service as a plain card: its name and what it covers. No icon, so nothing mismatched. */
const ServiceCard = ({ service, className }: { service: ServiceItem; className?: string }): React.JSX.Element => (
  <div className={cn("jobsite:border jobsite:border-border jobsite:bg-card jobsite:p-6 jobsite:shadow-sm", className)} style={SURFACE_RADIUS_STYLE}>
    <h3 className="jobsite:font-heading jobsite:text-xl jobsite:text-card-foreground">
      <ServiceName service={service} />
    </h3>
    {hasText(service.description) && (
      <p className="jobsite:mt-3 jobsite:text-base jobsite:leading-relaxed jobsite:text-muted-foreground">{service.description}</p>
    )}
  </div>
);

export type { ServicesLayoutProps };
export { ServiceCard, ServiceName, ServicesHeading, ServicesSection };
